import { useMemo, useState } from 'react';
import { lemon as Api, type LemonRequest, type LemonStatus } from '@tvx/api-client';
import { useAsync } from '../lib/useAsync';
import { Shim, SkeletonRows } from '../components/Shimmer';
import { Modal } from '../components/Modal';
import { Field } from '../components/Field';

type Filter = 'all' | LemonStatus;

const when = (v: string) => {
  const d = new Date(String(v).replace(' ', 'T') + 'Z');
  return isNaN(d.valueOf()) ? v
    : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

/** Free Telegram pass: a Lemon user ID, looked up in Lemon's partner panel and approved by hand. */
export function LemonRequests() {
  const { data, error, loading, reload } = useAsync((signal) => Api.list(signal));
  const [filter, setFilter] = useState<Filter>('pending');
  const [open, setOpen] = useState<LemonRequest | null>(null);
  const [days, setDays] = useState<string | null>(null);
  const [saved, setSaved] = useState('');

  const rows = useMemo(() => {
    const all = data?.requests ?? [];
    return filter === 'all' ? all : all.filter((r) => r.status === filter);
  }, [data, filter]);

  async function saveDays() {
    try { await Api.setDays(Number(days)); setSaved('Saved'); setDays(null); await reload(); }
    catch (e) { setSaved((e as Error).message); }
  }

  return (
    <div className="pg">
      <div className="rowbar">
        <div>
          <h1 style={{ fontSize: 29 }}>Telegram pass</h1>
          <p style={{ fontSize: 13.5, color: 'var(--tx2)', marginTop: 7 }}>
            {loading && !data ? <Shim w={210} h={12} style={{ marginTop: 3 }} />
              : data?.pending ? `${data.pending} Lemon ID${data.pending === 1 ? '' : 's'} waiting for review`
              : 'Nothing waiting for review.'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 12.5, color: 'var(--tx3)' }}>Trade Calls days on approval</span>
          <input className="inp" type="number" min={1} max={365} style={{ width: 80 }}
            value={days ?? String(data?.days ?? 7)} onChange={(e) => { setDays(e.target.value); setSaved(''); }} />
          {days !== null ? <button className="b bo" type="button" onClick={() => void saveDays()}>Save</button> : null}
          <span style={{ fontSize: 12, color: 'var(--tx3)' }}>{saved}</span>
        </div>
      </div>

      {error ? <div className="err">Could not load requests — {error.message}</div> : null}

      <div className="crd" style={{ padding: '20px 22px' }}>
        <div className="sg" style={{ marginBottom: 16 }}>
          {(['pending', 'approved', 'rejected', 'revoked', 'all'] as const).map((f) => (
            <span key={f} {...(filter === f ? { 'data-on': '' } : {})} onClick={() => setFilter(f)}>
              {f[0]!.toUpperCase() + f.slice(1)}
            </span>
          ))}
        </div>
        <div className="tblx">
          <table className="tbl">
            <thead><tr><th>Lemon ID</th><th>Member</th><th>Sent</th><th>Status</th><th>Telegram</th><th /></tr></thead>
            <tbody>
              {loading && !data ? <SkeletonRows cols={6} />
                : rows.length === 0 ? (
                  <tr><td colSpan={6} className="muted" style={{ padding: '26px 8px' }}>
                    {data?.requests.length ? 'Nothing with that status.' : 'No Lemon IDs yet.'}
                  </td></tr>
                ) : rows.map((r) => (
                  <tr key={r.id}>
                    <td style={{ fontWeight: 700, fontFamily: 'monospace' }}>{r.lemon_id}</td>
                    <td>
                      <div style={{ fontWeight: 700 }}>{r.name || '—'}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--tx3)' }}>
                        {[r.email, r.mobile, r.telegram && `@${r.telegram}`].filter(Boolean).join(' · ')}
                      </div>
                    </td>
                    <td className="muted" style={{ whiteSpace: 'nowrap' }}>{when(r.created_at)}</td>
                    <td><span className="tg">{r.status}</span></td>
                    <td className="muted">
                      {r.status !== 'approved' ? '—' : r.telegram_added_at ? `Added ${when(r.telegram_added_at)}` : 'To add'}
                    </td>
                    <td className="right">
                      <button className="b bo" style={{ padding: '6px 12px', fontSize: 11.5 }}
                        type="button" onClick={() => setOpen(r)}>
                        {r.status === 'pending' ? 'Review' : 'Open'}
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {open ? (
        <Review req={open} days={data?.days ?? 7} onCancel={() => setOpen(null)}
          onDone={async () => { setOpen(null); await reload(); }} />
      ) : null}
    </div>
  );
}

function Review({ req, days, onCancel, onDone }: {
  req: LemonRequest; days: number; onCancel: () => void; onDone: () => Promise<void>;
}) {
  const [note, setNote] = useState(req.note);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  async function act(f: Parameters<typeof Api.act>[1]) {
    setBusy('Saving…'); setError('');
    try { await Api.act(req.id, f); await onDone(); }
    catch (e) { setBusy(''); setError((e as Error).message); }
  }

  return (
    <Modal
      title={`Lemon ID · ${req.lemon_id}`}
      onClose={onCancel}
      footer={
        <>
          <span style={{ fontSize: 12, color: 'var(--tx3)' }}>{busy}</span>
          <span className="mdl-spacer" />
          <button className="b bo" type="button" onClick={onCancel}>Close</button>
          {req.status === 'pending' ? (
            <>
              <button className="b bo" type="button" onClick={() => void act({ decision: 'rejected', note })}>Reject</button>
              <button className="b bg" type="button" onClick={() => void act({ decision: 'approved', note })}>
                Approve · {days} days of calls
              </button>
            </>
          ) : null}
          {req.status === 'approved' ? (
            <>
              <button className="b bo" type="button" onClick={() => void act({ decision: 'revoked', note })}>Revoke</button>
              <button className="b bg" type="button"
                onClick={() => void act({ decision: 'telegram', added: !req.telegram_added_at })}>
                {req.telegram_added_at ? 'Mark not added' : 'Mark added to Telegram'}
              </button>
            </>
          ) : null}
        </>
      }
    >
      {error ? <div className="err">{error}</div> : null}
      <div style={{ fontSize: 13, lineHeight: 1.7, marginBottom: 14 }}>
        <div><strong>{req.name || '—'}</strong> · {[req.email, req.mobile].filter(Boolean).join(' · ')}</div>
        <div className="muted">Telegram: {req.telegram ? `@${req.telegram}` : 'not given'} · sent {when(req.created_at)}</div>
        {req.access_until ? <div className="muted">Trade Calls until {req.access_until}</div> : null}
        {req.decided_by ? <div className="muted">{req.status} by {req.decided_by}</div> : null}
      </div>
      <p className="muted" style={{ fontSize: 12.5, marginBottom: 12 }}>
        Look this ID up in Lemon's partner panel before approving. Approving opens Trade Calls for {days} days;
        add the member to the Telegram group yourself, then mark it added.
      </p>
      <Field label="Note to the member"
        hint="Required when rejecting. The member sees it, by push and email too.">
        <textarea className="inp ta" rows={3} value={note}
          readOnly={req.status === 'rejected' || req.status === 'revoked'}
          onChange={(e) => setNote(e.target.value)} />
      </Field>
    </Modal>
  );
}
